// client/src/pages/secure/SecurityPage.js
// "My Security": the signed-in user's own two-factor (authenticator app) set-up,
// recovery codes and turning it off. TwoFactorEnrollPage is the same set-up shown
// full-screen when the firm requires two-factor and this login has not got it.
import React from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';

const btn = 'text-sm px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-indigo-300';
const ghost = 'text-sm px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50';
const input = 'block w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-slate-500 focus:border-slate-500';
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US') : '');

function downloadCodes(codes, issuer, username) {
    const text = [
        `${issuer || 'Portal'} — two-factor recovery codes`,
        `Account: ${username || ''}`,
        `Generated: ${new Date().toLocaleString('en-US')}`,
        '',
        'Each code can be used ONCE to sign in if you do not have your phone.',
        'Keep them somewhere safe and private (not on the same phone).',
        '',
        ...codes,
        '',
    ].join('\r\n');
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `recovery-codes-${(username || 'account').replace(/[^\w-]/g, '')}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// The ten codes, shown once.
const RecoveryCodes = ({ codes, issuer, username, onDone, doneLabel = 'Done' }) => {
    const [saved, setSaved] = React.useState(false);
    const [copied, setCopied] = React.useState(false);
    const copy = async () => {
        try { await navigator.clipboard.writeText(codes.join('\n')); setCopied(true); } catch (e) { /* ignore */ }
    };
    return (
        <div className="space-y-3">
            <p className="text-sm text-slate-700">
                Save these recovery codes now. <strong>They will not be shown again.</strong> Each one lets you sign in once
                if your phone is lost or replaced.
            </p>
            <div className="grid grid-cols-2 gap-2 bg-slate-50 border border-slate-200 rounded-md p-3 font-mono text-sm select-all">
                {codes.map(c => <div key={c}>{c}</div>)}
            </div>
            <div className="flex flex-wrap gap-2">
                <button className={ghost} onClick={() => downloadCodes(codes, issuer, username)}>Download .txt</button>
                <button className={ghost} onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
                <button className={ghost} onClick={() => window.print()}>Print</button>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={saved} onChange={e => setSaved(e.target.checked)} />
                I have saved these codes somewhere safe.
            </label>
            <button className={btn} disabled={!saved} onClick={onDone}>{doneLabel}</button>
        </div>
    );
};

// Set-up: generate a key, add it to the app, confirm with a code.
const Enroll = ({ username, onEnabled, onCancel }) => {
    const api = useApiClient();
    const [setup, setSetup] = React.useState(null);
    const [code, setCode] = React.useState('');
    const [error, setError] = React.useState('');
    const [busy, setBusy] = React.useState(false);

    const start = React.useCallback(async () => {
        setError(''); setBusy(true);
        try { setSetup(await api.post('/api/auth/2fa/setup', {})); }
        catch (e) { setError(e.message); }
        finally { setBusy(false); }
    }, [api]);

    React.useEffect(() => { start(); }, [start]);

    const confirm = async (e) => {
        e.preventDefault();
        setError(''); setBusy(true);
        try {
            const r = await api.post('/api/auth/2fa/enable', { code });
            // A fresh session token comes back (it no longer carries the "must
            // enrol" hold). Adopt it before anything else asks the server.
            if (r.token) localStorage.setItem('jwtToken', r.token);
            onEnabled(r.recoveryCodes, setup.issuer);
        } catch (err) { setError(err.message); }
        finally { setBusy(false); }
    };

    if (!setup) {
        return (
            <div className="space-y-3">
                {busy && <p className="text-sm text-slate-500">Preparing…</p>}
                {error && <p className="text-sm text-red-600">{error}</p>}
                {error && <button className={ghost} onClick={start}>Try again</button>}
            </div>
        );
    }

    return (
        <div className="space-y-5">
            <ol className="space-y-5 text-sm text-slate-700">
                <li>
                    <div className="font-semibold text-slate-800">1. Install an authenticator app on your phone</div>
                    <p className="mt-1">Google Authenticator, Microsoft Authenticator, Authy or 1Password — any of them works.</p>
                </li>
                <li>
                    <div className="font-semibold text-slate-800">2. Scan this QR code with the app</div>
                    <p className="mt-1">In the app choose <em>Add account</em> → <em>Scan a QR code</em>, and point the phone's camera at this code:</p>
                    {setup.qrSvg && (
                        <div className="mt-3 flex justify-center">
                            <img alt="QR code for your authenticator app" width="220" height="220"
                                className="border border-slate-200 rounded-md bg-white p-2"
                                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(setup.qrSvg)}`} />
                        </div>
                    )}
                    <p className="mt-3">
                        Reading this on your phone? <a href={setup.otpauthUri} className="text-indigo-600 underline">Tap here to add it to your authenticator app</a> instead.
                    </p>
                    <details className="mt-3">
                        <summary className="cursor-pointer text-indigo-600 underline">Can't scan? Enter the key by hand</summary>
                        <p className="mt-2">In the app choose <em>Add account</em> → <em>Enter a setup key</em> (sometimes “Enter code manually”), then enter:</p>
                        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-md p-3 space-y-2">
                            <div><span className="text-xs text-slate-500 block">Account name</span>
                                <span className="font-medium">{setup.issuer} ({setup.account})</span></div>
                            <div><span className="text-xs text-slate-500 block">Key</span>
                                <span className="font-mono text-base font-semibold tracking-wider select-all break-all">{setup.secretGrouped}</span></div>
                            <div><span className="text-xs text-slate-500 block">Type</span>Time-based</div>
                        </div>
                    </details>
                </li>
                <li>
                    <div className="font-semibold text-slate-800">3. Enter the 6-digit code the app now shows</div>
                    <form onSubmit={confirm} className="mt-2 flex flex-wrap items-center gap-2">
                        <input className={`${input} max-w-[10rem] text-center font-mono text-lg tracking-widest`}
                            inputMode="numeric" autoComplete="one-time-code" maxLength={7} placeholder="123456"
                            value={code} onChange={e => setCode(e.target.value)} required autoFocus />
                        <button type="submit" className={btn} disabled={busy}>{busy ? 'Checking…' : 'Turn on two-factor'}</button>
                        {onCancel && <button type="button" className={ghost} onClick={onCancel}>Cancel</button>}
                    </form>
                    <p className="mt-1 text-xs text-slate-500">If it is rejected, check the phone's clock is set automatically and use the next code.</p>
                </li>
            </ol>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <p className="text-xs text-slate-500">Signed in as {username}. Keep the key private — anyone with it can generate your codes.</p>
        </div>
    );
};

// The whole two-factor panel: state, set-up, regenerate, turn off.
export const TwoFactorPanel = ({ user, forced = false }) => {
    const api = useApiClient();
    const [status, setStatus] = React.useState(null);
    const [mode, setMode] = React.useState('view'); // view | enroll | codes | regen | disable
    const [codes, setCodes] = React.useState(null);
    const [issuer, setIssuer] = React.useState('');
    const [form, setForm] = React.useState({ code: '', password: '', recoveryCode: '', useRecovery: false });
    const [error, setError] = React.useState('');
    const [flash, setFlash] = React.useState('');
    const [busy, setBusy] = React.useState(false);

    const load = React.useCallback(() => {
        api.get('/api/auth/2fa/status').then(setStatus).catch(e => setError(e.message));
    }, [api]);
    React.useEffect(() => { load(); }, [load]);

    const resetForm = () => setForm({ code: '', password: '', recoveryCode: '', useRecovery: false });

    const regenerate = async (e) => {
        e.preventDefault(); setError(''); setBusy(true);
        try {
            const r = await api.post('/api/auth/2fa/recovery-codes', { code: form.code });
            setCodes(r.recoveryCodes); setMode('codes'); resetForm();
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    const disable = async (e) => {
        e.preventDefault(); setError(''); setBusy(true);
        try {
            const body = { password: form.password, ...(form.useRecovery ? { recoveryCode: form.recoveryCode } : { code: form.code }) };
            const r = await api.post('/api/auth/2fa/disable', body);
            setFlash(r.message); setMode('view'); resetForm(); load();
        } catch (err) { setError(err.message); } finally { setBusy(false); }
    };

    if (!status) return error ? <p className="text-sm text-red-600">{error}</p> : <p className="text-sm text-slate-500">Loading…</p>;

    if (mode === 'enroll') {
        return <Enroll username={user?.username}
            onCancel={forced ? null : () => setMode('view')}
            onEnabled={(c, iss) => { setCodes(c); setIssuer(iss); setMode('codes'); }} />;
    }

    if (mode === 'codes') {
        return <RecoveryCodes codes={codes} issuer={issuer} username={user?.username}
            doneLabel={forced ? 'Continue to the portal' : 'Done'}
            onDone={() => {
                setCodes(null);
                // Reload so the app picks up the new session token.
                if (forced) { window.location.href = '/dashboard'; return; }
                setMode('view'); setFlash('Two-factor is on.'); load();
            }} />;
    }

    return (
        <div className="space-y-4">
            {flash && <div className="px-3 py-2 rounded border border-green-200 bg-green-50 text-sm text-green-700">{flash}</div>}
            {error && <div className="px-3 py-2 rounded border border-red-200 bg-red-50 text-sm text-red-700">{error}</div>}

            <div className="flex items-center gap-3">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${status.enabled ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                    {status.enabled ? 'ON' : 'OFF'}
                </span>
                <span className="text-sm text-slate-700">
                    {status.enabled
                        ? `Authenticator app set up ${fmtDate(status.enrolledAt)}. ${status.recoveryCodesRemaining} recovery code${status.recoveryCodesRemaining === 1 ? '' : 's'} left.`
                        : 'Signing in needs only your password.'}
                </span>
            </div>
            {status.required && !status.enabled && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    The brokerage requires two-factor for your account.
                </p>
            )}
            {status.enabled && status.recoveryCodesRemaining !== null && status.recoveryCodesRemaining <= 3 && (
                <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    You are running low on recovery codes. Generate a new set below.
                </p>
            )}

            {!status.enabled && (
                status.available
                    ? <button className={btn} onClick={() => { setError(''); setMode('enroll'); }}>Set up two-factor</button>
                    : <p className="text-sm text-slate-600">{status.unavailableReason}</p>
            )}

            {status.enabled && mode === 'view' && (
                <div className="flex flex-wrap gap-2">
                    <button className={ghost} onClick={() => { setError(''); resetForm(); setMode('regen'); }}>New recovery codes</button>
                    <button className={ghost} onClick={() => { setError(''); resetForm(); setMode('disable'); }}>Turn off two-factor</button>
                </div>
            )}

            {mode === 'regen' && (
                <form onSubmit={regenerate} className="border border-slate-200 rounded-md p-4 space-y-3 max-w-md">
                    <p className="text-sm text-slate-700">Enter a current code from your authenticator app. Your old recovery codes stop working.</p>
                    <input className={`${input} font-mono`} inputMode="numeric" autoComplete="one-time-code" maxLength={7}
                        placeholder="6-digit code" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} required />
                    <div className="flex gap-2">
                        <button className={btn} disabled={busy}>Generate new codes</button>
                        <button type="button" className={ghost} onClick={() => setMode('view')}>Cancel</button>
                    </div>
                </form>
            )}

            {mode === 'disable' && (
                <form onSubmit={disable} className="border border-red-200 rounded-md p-4 space-y-3 max-w-md">
                    <p className="text-sm text-slate-700">
                        To turn off two-factor, confirm your password and a code.
                        {status.required && ' The brokerage requires it for your account, so you will be asked to set it up again at your next sign-in.'}
                    </p>
                    <input type="password" className={input} autoComplete="current-password" placeholder="Password"
                        value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required />
                    {form.useRecovery
                        ? <input className={`${input} font-mono`} placeholder="Recovery code (xxxxx-xxxxx)"
                            value={form.recoveryCode} onChange={e => setForm({ ...form, recoveryCode: e.target.value })} required />
                        : <input className={`${input} font-mono`} inputMode="numeric" autoComplete="one-time-code" maxLength={7}
                            placeholder="6-digit code" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} required />}
                    <button type="button" className="text-xs text-indigo-600 hover:underline"
                        onClick={() => setForm({ ...form, useRecovery: !form.useRecovery })}>
                        {form.useRecovery ? 'Use the authenticator app instead' : 'Use a recovery code instead'}
                    </button>
                    <div className="flex gap-2">
                        <button className="text-sm px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-300" disabled={busy}>Turn off</button>
                        <button type="button" className={ghost} onClick={() => setMode('view')}>Cancel</button>
                    </div>
                </form>
            )}
        </div>
    );
};

const SecurityPage = ({ user }) => (
    <div className="max-w-3xl space-y-6">
        <div>
            <h1 className="text-2xl font-bold text-slate-800">My Security</h1>
            <p className="text-sm text-slate-500 mt-0.5">How you sign in to the portal.</p>
        </div>
        <section className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800">Two-factor sign-in (authenticator app)</h2>
            <p className="text-sm text-slate-500 mt-1 mb-4">
                After your password, the portal asks for a 6-digit code from an app on your phone. Someone who learns
                your password still cannot get in without the phone.
            </p>
            <TwoFactorPanel user={user} />
        </section>
        <section className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800">Password</h2>
            <p className="text-sm text-slate-500 mt-1"><Link to="/dashboard/change-password" className="text-indigo-600 hover:underline">Change your password</Link></p>
        </section>
        <section className="bg-white border border-slate-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-slate-800">Sessions</h2>
            <p className="text-sm text-slate-500 mt-1 mb-4">
                Left yourself signed in on a shared or lost device? This ends every session, everywhere, this one included.
            </p>
            <SignOutEverywhere />
        </section>
    </div>
);

// O6 (audit 2026-10): bumps this login's token version; every session token stops working.
const SignOutEverywhere = () => {
    const api = useApiClient();
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState('');
    const go = async () => {
        if (!window.confirm('Sign out on every device, including this one?')) return;
        setBusy(true);
        try {
            await api.post('/api/auth/sign-out-everywhere', {});
            localStorage.removeItem('jwtToken');
            window.location.href = '/portal';
        } catch (e) { setError(e.message); setBusy(false); }
    };
    return (
        <div>
            <button onClick={go} disabled={busy}
                className="text-sm px-4 py-2 bg-slate-800 text-white rounded-md hover:bg-slate-900 disabled:bg-slate-400">
                {busy ? 'Signing out…' : 'Sign out everywhere'}
            </button>
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </div>
    );
};

// Full-screen, no way past: the firm requires two-factor for this login.
export const TwoFactorEnrollPage = ({ user, onLogout }) => (
    <div className="flex items-center justify-center min-h-screen bg-slate-100 p-4">
        <div className="w-full max-w-xl p-8 bg-white rounded-xl shadow-lg space-y-4">
            <div>
                <h2 className="text-2xl font-bold text-gray-900">Set up two-factor sign-in</h2>
                <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                    The brokerage requires two-factor sign-in for your account. It takes about two minutes and you
                    only do it once.
                </p>
            </div>
            <ForcedEnroll user={user} />
            <div className="text-right">
                <button onClick={onLogout} className="text-sm text-slate-500 hover:underline">Sign out</button>
            </div>
        </div>
    </div>
);

// The forced screen goes straight into set-up. (The server never holds anyone
// here when it has no encryption key, so set-up is always possible.)
const ForcedEnroll = ({ user }) => {
    const [codes, setCodes] = React.useState(null);
    const [issuer, setIssuer] = React.useState('');
    if (codes) {
        return <RecoveryCodes codes={codes} issuer={issuer} username={user?.username}
            doneLabel="Continue to the portal" onDone={() => { window.location.href = '/dashboard'; }} />;
    }
    return <Enroll username={user?.username} onEnabled={(c, iss) => { setCodes(c); setIssuer(iss); }} />;
};

export default SecurityPage;
